import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from 'lucide-react';
import { Table } from '@tanstack/react-table';

import { Paginator } from 'components/Shared/Pagination/Paginator';
import { Button } from 'components/ui/button';
import type { Pagination } from 'interfaces/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import type { DataTableAppearance } from './types';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  appearance?: DataTableAppearance;
  alwaysShowPagination?: boolean;
  pagination?: Partial<Pagination>;
  pageSizeOptions?: number[];
}

export function DataTablePagination<TData>({
  table,
  appearance = 'default',
  alwaysShowPagination = false,
  pagination,
  pageSizeOptions,
}: DataTablePaginationProps<TData>) {
  const hasSelectedRows = table.getFilteredSelectedRowModel().rows.length > 0;
  const { pageIndex, pageSize } = table.getState().pagination;

  if (appearance === 'project-list') {
    return (
      <Paginator
        alwaysVisible={alwaysShowPagination}
        pagination={{
          currentPage: pageIndex + 1,
          lastPage: pagination?.lastPage ?? table.getPageCount(),
          total: pagination?.total ?? table.getFilteredRowModel().rows.length,
        }}
        onPageChange={(page) => table.setPageIndex(page - 1)}
        trailingContent={
          pageSizeOptions?.length ? (
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => table.setPageSize(Number(value))}
            >
              <SelectTrigger
                aria-label="Resultados por página"
                className="ml-3 h-8 w-[72px] rounded-lg bg-white px-2 font-GilroyRegular text-xs text-slate-700 hover:bg-gray-50 focus:ring-1 focus:ring-[#EE7C2C] focus:ring-offset-0"
                style={{
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  boxShadow: 'none',
                }}
              >
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent
                side="top"
                className="min-w-[72px] border-slate-200 bg-white font-GilroyRegular"
              >
                {pageSizeOptions.map((option) => (
                  <SelectItem
                    key={option}
                    value={`${option}`}
                    className="cursor-pointer pl-7 text-xs text-slate-700 focus:bg-orange-50 focus:text-slate-900 data-[state=checked]:bg-orange-50 data-[state=checked]:font-semibold"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : undefined
        }
      />
    );
  }

  return (
    <div className="flex w-full flex-col justify-between gap-4 px-2 font-sans sm:flex-row sm:items-center sm:gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        {hasSelectedRows && (
          <div className="font-normal text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length} de{' '}
            {table.getFilteredRowModel().rows.length} registros selecionados
          </div>
        )}

        <div className="flex items-center space-x-2">
          <p className="font-GilroyRegular text-sm font-medium">
            Registros por página
          </p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top" className="font-GilroyRegular">
              {[10, 20, 30, 40, 50].map((option) => (
                <SelectItem key={option} value={`${option}`}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-end sm:gap-6">
        <div className="font-GilroyRegular flex w-[120px] items-center justify-center text-sm font-medium">
          Página {pageIndex + 1} de {table.getPageCount()}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            className="font-GilroyRegular hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeftIcon className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            className="font-GilroyRegular h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Anterior</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            className="font-GilroyRegular h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Próxima</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            className="font-GilroyRegular hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
